/**
 * love-writeback-surfaces.ts
 *
 * Host surface allowlist for Love / Discuss / Go telemetry + Love writeback.
 * Pure — no interest-brief merge. Keep lockstep with
 * mobile/lib/crew/love-writeback-surfaces.ts (Metro cannot import web/).
 *
 * Server merge helpers live in love-writeback.ts and must not land in
 * client feed-api imports.
 *
 * Prefer these constants over string literals:
 * - your_crew → YourCrewHome / YourCrewScreen
 * - crew_feed → generic CrewFeedList default (CREW_FEED_DEFAULT_SURFACE)
 * - space_pulse → SpacePulse AI rows
 *
 * Do not invent new surfaces without updating writeback allowlist + hosts.
 */

export const CREW_FEED_SURFACE_YOUR_CREW = 'your_crew' as const
export const CREW_FEED_SURFACE_DEFAULT = 'crew_feed' as const
export const CREW_FEED_SURFACE_SPACE_PULSE = 'space_pulse' as const

/** Alias of CREW_FEED_SURFACE_DEFAULT — generic CrewFeedList / unmarked callers. */
export const CREW_FEED_DEFAULT_SURFACE = CREW_FEED_SURFACE_DEFAULT

/**
 * Closed set of host surface tags mirrored on mobile love-writeback-surfaces.
 * Prefer named CREW_FEED_SURFACE_* / CREW_FEED_DEFAULT_SURFACE at call sites;
 * use this object for parity iteration / drift audits only (not a second copy
 * source). Metro cannot import web/ — mobile mirrors the named exports only
 * (no CREW_FEED_SURFACE_MIRROR fork). Bun locks value equality in
 * `__tests__/lib/crew-feed-surface-constants-parity.test.ts`.
 */
export const CREW_FEED_SURFACE_MIRROR = {
  CREW_FEED_SURFACE_YOUR_CREW,
  CREW_FEED_SURFACE_DEFAULT,
  CREW_FEED_SURFACE_SPACE_PULSE,
  CREW_FEED_DEFAULT_SURFACE
} as const

/** Surfaces that should write Loved topics into the interest brief. */
export const CREW_LOVE_WRITEBACK_SURFACES = [
  CREW_FEED_SURFACE_YOUR_CREW,
  CREW_FEED_SURFACE_DEFAULT,
  CREW_FEED_SURFACE_SPACE_PULSE
] as const

export type CrewLoveWritebackSurface =
  (typeof CREW_LOVE_WRITEBACK_SURFACES)[number]

/** Same allowlist — use for telemetry surface typing in feed-api hosts. */
export const CREW_FEED_SURFACES = CREW_LOVE_WRITEBACK_SURFACES

export type CrewFeedSurface = CrewLoveWritebackSurface

/**
 * Gate Love writeback surfaces. Exact tag match only — no trim / case-fold
 * (padded or inventable tags must not unlock overnight whisper writeback).
 */
export function isCrewLoveWritebackSurface(
  surface: unknown
): surface is CrewLoveWritebackSurface {
  return (
    typeof surface === 'string' &&
    (CREW_LOVE_WRITEBACK_SURFACES as readonly string[]).includes(surface)
  )
}
